import React, { useState, useCallback } from 'react'
import styled from 'styled-components'

import { postRequest } from '../../../lib/http'
import useQuery from '../../../hooks/useQuery'
import config from '../../../config'

import { TailSpin as Loader } from 'react-loader-spinner'
import { toast } from 'react-toastify'
import { LeftAlignedPageContent } from '../../../components/LayoutComponents'
import isemail from 'isemail'
import { saveAs } from 'file-saver'
import {
  getFormSlugOrId,
  useFormsDefinitions,
} from '../../../providers/FormDefinitionsProvider'
import LoadingSpinner from '../../../components/LoadingSpinner'
import { findAndRemoveInfoElements } from '../../../lib/infoElementHelpers'
import sanitizeHtml from '../../../lib/sanitize-html'
import { useHelpMessage } from '../../../providers/HelpMessageProvider'

import {
  InfoPanel,
  InfoPanelGrey,
  InfoPanelGreyGap,
  PaymentDataCell,
  PaymentHeading,
  PaymentRow,
} from './ReceiptComponents'

const Heading = styled.span`
  font-size: 38px;
  margin-bottom: 8px;
`
Heading.displayName = 'Heading'

const ReferenceCode = styled.span`
  font-size: 46px;
  margin-bottom: 8px;
  font-weight: 800;
  color: ${({ theme }) => theme.palette.nswBrandDark};
`
ReferenceCode.displayName = 'ReferenceCode'

const InputWithButton = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`
InputWithButton.displayName = 'InputWithButton'

const ButtonContainer = styled.div`
  padding-top: 32px;
  padding-bottom: 32px;
`
ButtonContainer.displayName = 'ButtonContainer'

const PdfButtonContent = styled.div`
  display: flex;
  align-items: center;
`
PdfButtonContent.displayName = 'PdfButtonContent'

const LoaderStyled = (props: React.ComponentProps<typeof Loader>) => {
  return <Loader wrapperStyle={{ marginLeft: '1rem' }} {...props} />
}

function ReceiptLinkInfo({
  formId,
  receiptLink,
}: {
  formId?: number
  receiptLink: string
}) {
  if (formId === config.FORMS.BEEKEEPER_FORM_ID) {
    return (
      <ButtonContainer className="fa-download-pdf" style={{ paddingTop: 0 }}>
        <button
          className="button is-primary"
          onClick={() => {
            window.location.href = config.BEEKEEPER_RECEIPT_LINK
          }}
        >
          <span>Return to BFS Portal</span>
        </button>
      </ButtonContainer>
    )
  }
  return (
    <InfoPanel
      className="fa-info-panel fa-info-panel__receipt-link ql-editor"
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(receiptLink) }}
    />
  )
}

function GenericReceipt({ formId }: { formId?: number }) {
  const {
    trackingCode,
    submissionId,
    email,
    receiptNumber,
    amountPaid,
    westpacTotalAmount,
  } = useQuery()

  const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false)
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false)
  const [emailAddress, setEmailAddress] = useState<string>('')

  const { forms, isFetching } = useFormsDefinitions()

  const showPaymentSection = React.useMemo(
    () => receiptNumber || amountPaid || westpacTotalAmount,
    [amountPaid, receiptNumber, westpacTotalAmount],
  )
  const resendSubmissionEmail = useCallback(async () => {
    try {
      setIsSendingEmail(true)
      await postRequest({
        url: `${config.HOSTED_API_URL}/resend-submission-email`,
        resource: { submissionId, formId, emailAddress },
        addAuthHeaders: true,
      })
      toast.success('Email successfully sent', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } catch (e) {
      toast.error('There was an issue while trying to send your PDF email', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } finally {
      setIsSendingEmail(false)
    }
  }, [emailAddress, formId, submissionId])

  const downloadPdf = useCallback(async () => {
    try {
      setIsDownloadingPdf(true)
      const { fileName, data } = await postRequest<{
        fileName: string
        data: string
      }>({
        url: `${config.HOSTED_API_URL}/submission-pdf`,
        resource: { submissionId, formId, trackingCode },
        addAuthHeaders: true,
      })

      const resp = await fetch(`data:application/pdf;base64,${data}`)
      saveAs(await resp.blob(), fileName)
    } catch (e) {
      console.error(e)
      toast.error('There was an issue while trying to download your PDF', {
        position: 'bottom-right',
        hideProgressBar: true,
      })
    } finally {
      setIsDownloadingPdf(false)
    }
  }, [formId, submissionId, trackingCode])

  const onEmailAddressChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setEmailAddress(e.currentTarget.value)
    },
    [],
  )

  const isResendHidden = React.useMemo(() => {
    return formId === config.FORMS.BEEKEEPER_FORM_ID
  }, [formId])

  const goToLink = React.useMemo(() => {
    switch (config.DEPARTMENT) {
      case 'NSWFA':
        return (
          <>
            <a href="https://www.foodauthority.nsw.gov.au/">
              NSW Food Authority website
            </a>
            .
          </>
        )
      default:
        return (
          <>
            <a href="https://www.dpi.nsw.gov.au/">
              Department of Primary Industries and Regional Development website
            </a>
            .
          </>
        )
    }
  }, [])

  const formDefinition = React.useMemo(() => {
    if (forms) {
      return forms.find((f) => f.id === formId)
    }
  }, [forms, formId])

  const receiptInfoHTML = React.useMemo(() => {
    if (formDefinition) {
      return findAndRemoveInfoElements(formDefinition.elements)
    }
  }, [formDefinition])

  const receiptMessage = React.useMemo(() => {
    return receiptInfoHTML?.receiptMessage
  }, [receiptInfoHTML])

  const receiptLink = React.useMemo(() => {
    return receiptInfoHTML?.receiptLink
  }, [receiptInfoHTML])

  const linkToRestart = React.useMemo(() => {
    return receiptInfoHTML?.linkToRestart
  }, [receiptInfoHTML])

  const contactMessage = React.useMemo(() => {
    return receiptInfoHTML?.contactMessage
  }, [receiptInfoHTML])

  const { setHelpMessage } = useHelpMessage()

  React.useEffect(() => {
    const helpMessage = receiptInfoHTML?.helpMessage
    if (helpMessage) {
      setHelpMessage(helpMessage)
    }

    return () => {
      setHelpMessage('')
    }
  })

  if (isFetching) {
    return <LoadingSpinner>Loading</LoadingSpinner>
  }

  return (
    <LeftAlignedPageContent className="fa-form-receipt">
      {receiptMessage && (
        <InfoPanel
          className="fa-info-panel fa-info-panel__receipt-message ql-editor"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(receiptMessage) }}
        />
      )}
      {showPaymentSection && (
        <InfoPanelGreyGap className="fa-info-panel fa-info-panel__payment-details">
          <PaymentHeading>Thank you for your payment</PaymentHeading>
          <b>Payment Summary</b>
          <div>
            {receiptNumber && (
              <PaymentRow>
                <p>Receipt Number</p>
                <PaymentDataCell>{receiptNumber.toString()}</PaymentDataCell>
              </PaymentRow>
            )}
            {westpacTotalAmount && (
              <PaymentRow>
                <p>Total Amount</p>
                <PaymentDataCell>
                  {westpacTotalAmount.toString()}
                </PaymentDataCell>
              </PaymentRow>
            )}
          </div>
        </InfoPanelGreyGap>
      )}
      <InfoPanel className="fa-info-panel ob-information__content ql-editor">
        <b>Your reference code is:</b>
        <ReferenceCode className="fa-info-panel__tracking-code">
          {trackingCode?.toString()}
        </ReferenceCode>
      </InfoPanel>
      <InfoPanelGrey className="fa-info-panel fa-info-panel__grey">
        <span>
          Please <b>record</b> this reference code. You can quote the code above
          when enquiring about your form submission.
        </span>
      </InfoPanelGrey>
      {email && (
        <InfoPanel>
          <span>
            A copy of your submission has been sent to your email address:{' '}
            <b>{email.toString()}</b>
          </span>
          <br />
          <span>
            If you have not received it or would like another copy, please
            select one of the options below:
          </span>
        </InfoPanel>
      )}
      <ButtonContainer className="fa-download-pdf">
        <button
          className="button is-primary"
          disabled={isDownloadingPdf}
          onClick={downloadPdf}
        >
          {isDownloadingPdf ? (
            <PdfButtonContent>
              <span>Downloading PDF</span>
              <LoaderStyled height={28} width={28} />
            </PdfButtonContent>
          ) : (
            <span>Download a PDF copy of your form</span>
          )}
        </button>
      </ButtonContainer>
      {!isResendHidden && (
        <>
          <label className="fa-email-pdf__label">
            Send yourself an email with a PDF copy for your records.
          </label>
          <InputWithButton className="fa-email-pdf__input">
            <input
              type="email"
              className="input ob-input"
              onChange={onEmailAddressChange}
            ></input>

            <button
              className="button is-primary ob-lookup__button"
              onClick={resendSubmissionEmail}
              disabled={!isemail.validate(emailAddress) || isSendingEmail}
            >
              {isSendingEmail ? (
                <LoaderStyled height={28} width={28} />
              ) : (
                <span>Email</span>
              )}
            </button>
          </InputWithButton>
        </>
      )}

      {contactMessage ? (
        <InfoPanel
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(contactMessage) }}
        />
      ) : (
        <InfoPanel>
          <span>
            If you are experiencing issues, please contact{' '}
            <b>
              <a href="mailto:bfs.admin@dpi.nsw.gov.au">
                bfs.admin@dpi.nsw.gov.au
              </a>
              .
            </b>
          </span>
        </InfoPanel>
      )}

      <InfoPanel>
        <span>Go to {goToLink}</span>
      </InfoPanel>

      {linkToRestart && formDefinition && (
        <InfoPanel className="fa-info-panel fa-info-panel__restart_link">
          <span>
            Create a new{' '}
            <a href={`/forms/${getFormSlugOrId(formDefinition)}`}>
              {formDefinition?.name || 'form'}
            </a>
            .
          </span>
        </InfoPanel>
      )}
      {receiptLink && (
        <ReceiptLinkInfo formId={formId} receiptLink={receiptLink} />
      )}
    </LeftAlignedPageContent>
  )
}
export default GenericReceipt
